import { css } from "@emotion/react";
import React from "react";
import { Helmet } from "react-helmet";
import { FaQuestionCircle } from "react-icons/fa";
import Modal from "react-modal";
import { AsOf } from "../shared/AsOf";
import DetailedChart from "../shared/DetailedChart";
import { DownloadsLabel } from "../shared/DownloadsLabel";
import ExportButton from "../shared/ExportButton";
import {
    AllTimeDownloadsHelp,
    AverageDownloadsPerEpisodeHelp,
    EpisodeGrowthHelp,
    ShowDetailsHelp,
    ThirtyDayAudienceHelp,
    ThirtyDayDownloadsHelp,
    TopAppsHelp,
    TopCountriesHelp
} from "../shared/MetricsHelp";
import NoMeasurement from "../shared/NoMeasurement";
import PaidFeatureButton from "../shared/PaidFeatureButton";
import { useAccount, useAccountID } from "../store/account";
import { useShow, useShowID } from "../store/show";
import BigMetric from "./BigMetric";
import EpisodeGrowth from "./EpsiodeGrowth";
import MetricNav from "./MetricNav";
import TopClients from "./TopClients";
import TopCountries from "./TopCountries";
import "./show-details.scss";

const modalStyles = {
    content: {
        top: "10%",
        left: "10%",
        right: "10%",
        bottom: "auto"
    }
};

Modal.setAppElement("#root");
Modal.defaultStyles.overlay.backgroundColor = "#666666cc";

export default function ShowDetails() {
    const accountID = useAccountID();
    const showID = useShowID();

    const { data: account } = useAccount();

    const { isLoading, data: show } = useShow(accountID, showID) || {};

    const [downloadGraphs, setDownloadGraphs] = React.useState("globalDownloads");
    const [episodeGraphs, setEpisodeGraphs] = React.useState("episodeGrowth");
    const [modal, setModal] = React.useState();

    const downloadsLabel = DownloadsLabel();

    const openModal = (name, help, region) => event => {
        setModal({ name, help, region });
        event.stopPropagation();
    };

    function closeModal() {
        setModal();
    }

    return (
        <>
            <Helmet>
                <title>Show Overview - Podtrac</title>
            </Helmet>
            {show ? (
                show.hasMeasurement ? (
                    <>
                        <div
                            css={css`
                                display: grid;
                                grid-column-gap: 1rem;
                                align-items: center;
                                grid-template-columns: 1fr auto;
                                h1 {
                                    margin-bottom: 0;
                                }
                            `}
                        >
                            <h1>Show Overview</h1>
                            <div
                                onClick={openModal(
                                    "Show Overview - About this Report",
                                    ShowDetailsHelp
                                )}
                                css={css`
                                    display: inline-block;
                                    font-size: 0.65rem;
                                    line-height: 1rem;
                                    color: var(--podtrac-blue-darker);
                                    cursor: pointer;
                                    text-transform: none;
                                `}
                            >
                                about this report{" "}
                                <FaQuestionCircle style={{ verticalAlign: "text-top" }} />
                            </div>
                            <LastMeasured network={account} />
                            {account && (account.isPaidPlan || account.offerUpgrade) ? (
                                <PaidFeatureButton
                                    hasFeature={account.isPaidPlan}
                                    dialogContent="Export of this report to an Excel file is a premium feature available in the Grow Your Show Plan."
                                >
                                    {({ hasFeature, openModal }) => {
                                        return (
                                            <ExportButton
                                                onClick={
                                                    hasFeature
                                                        ? () =>
                                                              (window.location = `/__/${account.accountID}/reports/showoverviewexport/${showID}`)
                                                        : openModal
                                                }
                                            />
                                        );
                                    }}
                                </PaidFeatureButton>
                            ) : null}
                        </div>

                        { !show.youTubeShow ? ( <>
                            <h2>All-Time <DownloadsLabel /></h2>
                            <div
                                css={css`
                                    padding: 1rem;
                                    margin-bottom: 2rem;
                                    border: 1px solid #ddd;
                                    page-break-inside: avoid;
                                    break-inside: avoid;
                                `}
                            >
                                <div
                                    css={css`
                                        display: grid;
                                        grid-template-columns: 1fr 3fr;
                                        grid-gap: 0.5rem;
                                        align-items: center;
                                    `}
                                >
                                    <BigMetric
                                        range="All-Time"
                                        name={downloadsLabel}
                                        region="Global"
                                        value={show.globalDownloadsAllTime}
                                        help={AllTimeDownloadsHelp}
                                        openModal={openModal}
                                        nogrowth
                                    />
                                    <DetailedChart
                                        data={show.sparklines}
                                        index={"globalDownloads"}
                                        dateIndex={"measurementDate"}
                                        name={"All-Time Global " + downloadsLabel}
                                        height="7rem"
                                        margin={0}
                                        yTicks={2}
                                        noborder
                                    />
                                </div>
                            </div>

                            <h2>30-Day Downloads and Audience</h2>
                            <div
                                css={css`
                                    padding: 1rem;
                                    margin-bottom: 2rem;
                                    border: 1px solid #ddd;
                                    page-break-inside: avoid;
                                    break-inside: avoid;
                                `}
                            >
                                <MetricNav>
                                    <MetricNav.Items>
                                        <BigMetric
                                            range="30-Day"
                                            name="Downloads"
                                            region="Global"
                                            value={show.globalDownloads30Day}
                                            prevValue={show.globalDownloads30DayPrevious}
                                            help={ThirtyDayDownloadsHelp}
                                            openModal={openModal}
                                            metricKey="globalDownloads"
                                            currentKey={downloadGraphs}
                                            setKey={setDownloadGraphs}
                                        />
                                        <BigMetric
                                            range="30-Day"
                                            name="Downloads"
                                            region="United States"
                                            value={show.usDownloads30Day}
                                            prevValue={show.usDownloads30DayPrevious}
                                            help={ThirtyDayDownloadsHelp}
                                            openModal={openModal}
                                            metricKey="usDownloads"
                                            currentKey={downloadGraphs}
                                            setKey={setDownloadGraphs}
                                        />
                                        <BigMetric
                                            range="30-Day"
                                            name="Audience"
                                            region="Global"
                                            value={show.globalAudience30Day}
                                            prevValue={show.globalAudience30DayPrevious}
                                            help={ThirtyDayAudienceHelp}
                                            openModal={openModal}
                                            metricKey="globalAudience"
                                            currentKey={downloadGraphs}
                                            setKey={setDownloadGraphs}
                                        />
                                        <BigMetric
                                            range="30-Day"
                                            name="Audience"
                                            region="United States"
                                            value={show.usAudience30Day}
                                            prevValue={show.usAudience30DayPrevious}
                                            help={ThirtyDayAudienceHelp}
                                            openModal={openModal}
                                            metricKey="usAudience"
                                            currentKey={downloadGraphs}
                                            setKey={setDownloadGraphs}
                                        />
                                    </MetricNav.Items>
                                </MetricNav>
                                {downloadGraphs == "globalDownloads" ? (
                                    <DetailedChart
                                        data={show.sparklines}
                                        index={"globalDownloads30Day"}
                                        prevIndex={"globalDownloads30DayPrevious"}
                                        dateIndex={"measurementDate"}
                                        name="30-Day Global Downloads"
                                        margin={0}
                                        noborder
                                    />
                                ) : downloadGraphs == "usDownloads" ? (
                                    <DetailedChart
                                        data={show.sparklines}
                                        index={"usDownloads30Day"}
                                        prevIndex={"usDownloads30DayPrevious"}
                                        dateIndex={"measurementDate"}
                                        name="30-Day US Downloads"
                                        margin={0}
                                        noborder
                                    />
                                ) : downloadGraphs == "globalAudience" ? (
                                    <DetailedChart
                                        data={show.sparklines}
                                        index={"globalAudience30Day"}
                                        prevIndex={"globalAudience30DayPrevious"}
                                        dateIndex={"measurementDate"}
                                        name="30-Day Global Audience"
                                        margin={0}
                                        noborder
                                    />
                                ) : downloadGraphs == "usAudience" ? (
                                    <DetailedChart
                                        data={show.sparklines}
                                        index={"usAudience30Day"}
                                        prevIndex={"usAudience30DayPrevious"}
                                        dateIndex={"measurementDate"}
                                        name="30-Day US Audience"
                                        margin={0}
                                        noborder
                                    />
                                ) : (
                                    ""
                                )}
                            </div>
                        </> ) : (
                            ""
                        )}

                        <h2>Episode Trends</h2>
                        <div
                            css={css`
                                padding: 1rem;
                                margin-bottom: 2rem;
                                border: 1px solid #ddd;
                                page-break-inside: avoid;
                                break-inside: avoid;
                                & a {
                                    display: block;
                                    text-align: right;
                                    margin: 1em 0 0;
                                    text-decoration: none;
                                    &:hover {
                                        text-decoration: underline;
                                    }
                                }
                            `}
                        >
                            <MetricNav>
                                <MetricNav.Items>
                                    <BigMetric
                                        range="7-Day"
                                        name="Episode Delivery"
                                        region="Global"
                                        openModal={openModal}
                                        help={EpisodeGrowthHelp}
                                        nometric
                                        nogrowth
                                        metricKey="episodeGrowth"
                                        currentKey={episodeGraphs}
                                        setKey={setEpisodeGraphs}
                                    />
                                    <BigMetric
                                        range="Average 7-Day"
                                        name={downloadsLabel + "/Episode"}
                                        region="Global"
                                        value={show.averageSevenDayGlobalDownloads}
                                        prevValue={show.averageSevenDayGlobalDownloadsPrevious}
                                        data={show.sparklines}
                                        openModal={openModal}
                                        help={AverageDownloadsPerEpisodeHelp}
                                        metricKey="globalEpisodeDownloads"
                                        currentKey={episodeGraphs}
                                        setKey={setEpisodeGraphs}
                                        visible={!show.youTubeShow}
                                    />
                                    <BigMetric
                                        range="Average 7-Day"
                                        name={downloadsLabel + "/Episode"}
                                        region="United States"
                                        value={show.averageSevenDayUSDownloads}
                                        prevValue={show.averageSevenDayUSDownloadsPrevious}
                                        data={show.sparklines}
                                        openModal={openModal}
                                        help={AverageDownloadsPerEpisodeHelp}
                                        metricKey="usEpisodeDownloads"
                                        currentKey={episodeGraphs}
                                        setKey={setEpisodeGraphs}
                                        visible={!show.youTubeShow}
                                    />
                                </MetricNav.Items>
                                {episodeGraphs == "episodeGrowth" ? (
                                    <EpisodeGrowth accountID={accountID} showID={showID} />
                                ) : episodeGraphs == "globalEpisodeDownloads" ? (
                                    <DetailedChart
                                        data={show.sparklines}
                                        index={"averageSevenDayGlobalDownloads"}
                                        prevIndex={"averageSevenDayGlobalDownloadsPrevious"}
                                        dateIndex={"measurementDate"}
                                        name={"Avg. 7-Day Global " + downloadsLabel + "/Episode"}
                                        margin={0}
                                        noborder
                                    />
                                ) : episodeGraphs == "usEpisodeDownloads" ? (
                                    <DetailedChart
                                        data={show.sparklines}
                                        index={"averageSevenDayUSDownloads"}
                                        prevIndex={"averageSevenDayUSDownloadsPrevious"}
                                        dateIndex={"measurementDate"}
                                        name={"Avg. 7-Day US " + downloadsLabel + "/Episode"}
                                        margin={0}
                                        noborder
                                    />
                                ) : (
                                    ""
                                )}
                            </MetricNav>
                            {/*<a*/}
                            {/*    href={`/dashboard/${accountID}/reports/measurement/beta/${showID}/episode`}*/}
                            {/*>*/}
                            {/*    Downloads by episode ▶*/}
                            {/*</a>*/}
                        </div>
                        <div
                            css={css`
                                display: grid;
                                grid-column-gap: 2rem;
                                grid-template-columns: 1fr 1fr;
                                margin-bottom: 2rem;
                                page-break-inside: avoid;
                                break-inside: avoid;
                                & table {
                                    width: 100%;
                                    margin: 0;
                                }
                                & a {
                                    display: block;
                                    text-align: right;
                                    margin: 1em 0 0;
                                    text-decoration: none;
                                    &:hover {
                                        text-decoration: underline;
                                    }
                                }
                            `}
                        >
                            <h2>
                                Top Countries{" "}
                                <span
                                    onClick={openModal("Top Countries", TopCountriesHelp)}
                                    css={css`
                                        font-size: 0.75rem;
                                        color: var(--podtrac-blue-darker);
                                        cursor: pointer;
                                        text-transform: none;
                                        vertical-align: top;
                                    `}
                                >
                                    <FaQuestionCircle />
                                </span>
                            </h2>
                            <h2>
                                Top Apps{" "}
                                <span
                                    onClick={openModal("Top Apps", TopAppsHelp)}
                                    css={css`
                                        font-size: 0.75rem;
                                        color: var(--podtrac-blue-darker);
                                        cursor: pointer;
                                        text-transform: none;
                                        vertical-align: top;
                                    `}
                                >
                                    <FaQuestionCircle />
                                </span>
                            </h2>
                            <div
                                css={css`
                                    padding: 1rem;
                                    border: 1px solid #ddd;
                                `}
                            >
                                <TopCountries />
                                {/*<a href={`/dashboard/${accountID}/reports/measurement/beta/${showID}/country`}>*/}
                                {/*    Downloads by country ▶*/}
                                {/*</a>*/}
                            </div>
                            <div
                                css={css`
                                    padding: 1rem;
                                    border: 1px solid #ddd;
                                `}
                            >
                                <TopClients />
                                {/*<a href={`/dashboard/${accountID}/reports/measurement/beta/${showID}/client`}>*/}
                                {/*    Downloads by client ▶*/}
                                {/*</a>*/}
                            </div>
                        </div>
                        <MetricModal modal={modal} closeModal={closeModal} />
                    </>
                ) : (
                    <div style={{ margin: "1em 0" }}>
                        <NoMeasurement show={show} />
                    </div>
                )
            ) : null}
            {/*<pre>{JSON.stringify(showMetricsQuery, null, 2)}</pre>*/}
        </>
    );
}

function MetricModal({ modal, closeModal }) {
    const { help: Help } = modal || {};
    return (
        <Modal
            isOpen={!!modal}
            onRequestClose={closeModal}
            style={modalStyles}
            contentLabel={modal && modal.name}
        >
            {modal && (
                <>
                    <div
                        css={css`
                            display: flex;
                            align-items: center;
                            & > h1 {
                                margin: 0;
                                font-size: 1.5rem;
                                font-weight: 500;
                                color: #000;
                            }
                            margin-bottom: 1rem;
                        `}
                    >
                        <h1
                            css={css`
                                flex: 1;
                            `}
                        >
                            {modal.name}
                        </h1>
                        <div
                            onClick={closeModal}
                            css={css`
                                font-size: 1.5rem;
                                font-weight: 500;
                                color: #999;
                                cursor: pointer;
                                &:hover {
                                    color: #000;
                                }
                            `}
                        >
                            ×
                        </div>
                    </div>
                    <div
                        css={css`
                            max-height: 60vh;
                            overflow: auto;
                            padding-right: 0.5rem;
                        `}
                    >
                        <Help region={modal.region} />
                    </div>
                </>
            )}
        </Modal>
    );
}

const LastMeasured = ({ network }) => (
    <div>{network ? <AsOf date={network.lastMeasured} /> : null}</div>
);
